import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useNavigate } from 'react-router-dom';

const initialState = {
  email: '',
  password: '',
};

const initialErrors = {
  email: '',
  password: '',
};

const LoginPage = () => {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState(initialErrors);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const navigate = useNavigate();

  const validate = () => {
    const newErrors = { ...initialErrors };
    let valid = true;
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Please enter a valid email address.';
      valid = false;
    }
    if (!form.password) {
      newErrors.password = 'Password is required.';
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleForgotPassword = async () => {
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setErrors(prev => ({ ...prev, email: 'Enter your email above, then click reset.' }));
      return;
    }
    setSubmitting(true);
    setMessage({ text: '', type: '' });
    try {
      await sendPasswordResetEmail(auth, form.email);
      // Firebase returns the same success whether or not the account exists (avoids leaking accounts)
      setMessage({ text: 'If an account exists for that email, a reset link has been sent.', type: 'success' });
    } catch (error: any) {
      const msg = error.code === 'auth/invalid-email' ? 'Invalid email address.'
        : 'Could not send reset email. Please try again.';
      setMessage({ text: msg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      setMessage({ text: 'Please correct the errors in the form.', type: 'error' });
      return;
    }
    setSubmitting(true);
    setMessage({ text: '', type: '' });
    try {
      await signInWithEmailAndPassword(auth, form.email, form.password);
      setMessage({ text: 'Login successful! Redirecting...', type: 'success' });
      setTimeout(() => {
        setMessage({ text: '', type: '' });
        navigate('/');
      }, 1000);
    } catch (error: any) {
      let errorMsg = 'Login failed. Please try again.';
      // Map Firebase Auth error codes to user-friendly messages
      console.log(error.code);
      switch (error.code) {
        case 'auth/user-not-found':
          errorMsg = 'No user found with this email.';
          break;
        case 'auth/wrong-password':
          errorMsg = 'Incorrect password.';
          break;
        case 'auth/invalid-email':
          errorMsg = 'Invalid email address.';
          break;
        case 'auth/invalid-credential':
          errorMsg = 'Invalid email or password.';
          break;
        case 'auth/too-many-requests':
          errorMsg = 'Too many failed attempts. Please try again later or reset your password.';
          break;
        case 'auth/user-disabled':
          errorMsg = 'This account has been disabled. Please contact support.';
          break;
        default:
          if (error.message) {
            errorMsg = error.message.replace(/^Firebase: | \(auth\/.+\)\.?$/g, '').trim() || errorMsg;
          }
          break;
      }
      setMessage({ text: errorMsg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="registration-container">
      <div className="registration-header">
        <h1>Sign In to HSI</h1>
        <p>Welcome back! Please log in to your account.</p>
      </div>
      {message.text && (
        <div className={`message-box show ${message.type}`}>{message.text}</div>
      )}
      <form className="registration-form" onSubmit={handleSubmit} noValidate>
        <fieldset>
          <legend>Login</legend>
          <div className="form-grid">
            <div className="form-group full-width">
              <label htmlFor="email">Email Address</label>
              <input type="email" id="email" name="email" value={form.email} onChange={handleChange} required disabled={submitting} />
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>
            <div className="form-group full-width">
              <label htmlFor="password">Password</label>
              <input type="password" id="password" name="password" value={form.password} onChange={handleChange} required disabled={submitting} />
              {errors.password && <div className="error-message">{errors.password}</div>}
            </div>
          </div>
        </fieldset>
        <button type="submit" className="submit-btn" disabled={submitting}>
          {submitting ? 'Signing In...' : 'Sign In'}
        </button>
        <p className="forgot-link">
          <button type="button" onClick={handleForgotPassword} disabled={submitting}>Forgot password?</button>
        </p>
      </form>
      <p className="login-link">
        Don&apos;t have an account? <a href="/register">Register</a>
      </p>
      <style>{`
        :root {
          --primary-color: #00796b;
          --secondary-color: #f9a825;
          --background-color: #f4f6f8;
          --form-background: #ffffff;
          --text-color: #333333;
          --light-gray: #e0e0e0;
          --error-color: #c62828;
          --success-color: #2e7d32;
        }
        body { background: none; }
        .registration-container {
          background-color: var(--form-background);
          padding: 30px 40px;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          width: 100%;
          max-width: 400px;
          box-sizing: border-box;
          margin: 60px auto;
        }
        .registration-header { text-align: center; margin-bottom: 30px; }
        .registration-header h1 { color: var(--primary-color); margin: 0 0 10px 0; font-size: 28px; }
        .registration-header p { color: var(--text-color); margin: 0; font-size: 16px; }
        .registration-form fieldset { border: 1px solid var(--light-gray); border-radius: 8px; padding: 20px; margin-bottom: 20px; }
        .registration-form legend { padding: 0 10px; font-weight: 600; color: var(--primary-color); }
        .form-grid { display: grid; grid-template-columns: 1fr; gap: 20px; }
        .form-group { display: flex; flex-direction: column; }
        .form-group label { margin-bottom: 8px; font-weight: 600; font-size: 14px; color: var(--text-color); }
        .form-group input { padding: 12px 15px; border: 1px solid var(--light-gray); border-radius: 6px; font-size: 16px; transition: border-color 0.3s, box-shadow 0.3s; width: 100%; box-sizing: border-box; }
        .form-group input:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(0, 121, 107, 0.2); }
        .error-message { color: var(--error-color); font-size: 12px; margin-top: 5px; }
        .submit-btn { width: 100%; padding: 15px; background-color: var(--primary-color); color: white; border: none; border-radius: 6px; font-size: 18px; font-weight: 600; cursor: pointer; transition: background-color 0.3s, transform 0.2s; }
        .submit-btn:hover { background-color: #005a4f; }
        .submit-btn:active { transform: scale(0.98); }
        .submit-btn:disabled { background-color: #9e9e9e; cursor: not-allowed; }
        .forgot-link { text-align: center; margin-top: 15px; font-size: 14px; }
        .forgot-link button { background: none; border: none; padding: 0; color: var(--primary-color); font-weight: 600; font-size: 14px; cursor: pointer; }
        .forgot-link button:hover { text-decoration: underline; }
        .forgot-link button:disabled { color: #9e9e9e; cursor: not-allowed; }
        .login-link { text-align: center; margin-top: 20px; font-size: 14px; }
        .login-link a { color: var(--primary-color); font-weight: 600; text-decoration: none; }
        .login-link a:hover { text-decoration: underline; }
        .message-box { position: fixed; top: 20px; right: 20px; padding: 15px 20px; border-radius: 8px; color: white; font-size: 16px; z-index: 1000; opacity: 0; transform: translateY(-50px); transition: opacity 0.5s, transform 0.5s; }
        .message-box.show { opacity: 1; transform: translateY(0); }
        .message-box.success { background-color: var(--success-color); }
        .message-box.error { background-color: var(--error-color); }
      `}</style>
    </div>
  );
};

export default LoginPage; 